"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { useMemo, type ReactNode } from "react";
import { ABOUT_4_SEED_METADATA } from "@/constants/admin/fields/about/about_4";

type FooterRow = {
  id?: string;
  column_index?: string;
  row_type?: string;
  section_title?: string;
  link_text?: string;
  link_href?: string;
  rich_html?: string;
  icon?: string;
  icon_alt?: string;
  kv_label?: string;
  kv_value?: string;
  priority?: number;
  status?: boolean;
};

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href);
}

function sortRows(rows: FooterRow[]) {
  return [...rows].sort((a, b) => {
    const pa = Number(a.priority ?? 0);
    const pb = Number(b.priority ?? 0);
    if (pa !== pb) return pa - pb;
    return String(a.id ?? "").localeCompare(String(b.id ?? ""));
  });
}

function SectionHeader({
  title,
  accentColor,
  textColor,
}: {
  title: string;
  accentColor: string;
  textColor: string;
}) {
  const t = title.trim();
  if (!t) return null;
  return (
    <div
      className="inline-flex max-w-full border-b-2 border-solid pb-0"
      style={{ borderColor: accentColor }}
    >
      <h3
        className="text-[20px] font-semibold leading-normal tracking-normal min-[640px]:text-[22px] min-[1024px]:text-[24px]"
        style={{ color: textColor }}
      >
        {t}
      </h3>
    </div>
  );
}

function FooterTextLink({
  label,
  href,
  textColor,
}: {
  label: string;
  href: string;
  textColor: string;
}) {
  const h = normalizeHref(href);
  const body = (
    <span
      className="text-[13px] font-normal leading-normal tracking-normal min-[640px]:text-[14px]"
      style={{ color: textColor }}
    >
      {label}
    </span>
  );
  if (!h) {
    return <div className="text-center min-[1024px]:text-left">{body}</div>;
  }
  if (isExternalHref(h)) {
    return (
      <a
        href={h}
        target="_blank"
        rel="noopener noreferrer"
        className="text-center transition-opacity hover:opacity-85 min-[1024px]:text-left"
      >
        {body}
      </a>
    );
  }
  return (
    <Link
      href={h}
      className="text-center transition-opacity hover:opacity-85 min-[1024px]:text-left"
    >
      {body}
    </Link>
  );
}

function SocialButton({
  href,
  icon,
  alt,
  textColor,
}: {
  href: string;
  icon?: string;
  alt?: string;
  textColor: string;
}) {
  const h = normalizeHref(href);
  const size = 30;
  const inner = icon ? (
    <Image
      src={icon}
      alt={alt || "Social"}
      width={size}
      height={size}
      className="size-[30px] rounded-full object-cover"
    />
  ) : (
    <span
      className="flex size-[30px] items-center justify-center rounded-full bg-white/15 text-[10px] font-semibold"
      style={{ color: textColor }}
      aria-hidden
    >
      ●
    </span>
  );
  if (!h) {
    return <div className="shrink-0">{inner}</div>;
  }
  return (
    <a
      href={h}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 transition-opacity hover:opacity-85"
      aria-label={alt || "Social link"}
    >
      {inner}
    </a>
  );
}

function renderUnderHeadingChunk(
  chunk: FooterRow[],
  textColor: string,
  chunkKey: string,
): ReactNode {
  const nodes: ReactNode[] = [];
  let j = 0;
  while (j < chunk.length) {
    const r = chunk[j];
    const rt = String(r.row_type || "").trim();

    if (rt === "link") {
      const links: FooterRow[] = [];
      while (j < chunk.length && String(chunk[j].row_type) === "link") {
        links.push(chunk[j]);
        j++;
      }
      nodes.push(
        <div
          key={`${chunkKey}-links-${j}`}
          className="flex w-full flex-col gap-3.5 min-[1024px]:pl-0.5"
        >
          {links.map((l) => (
            <FooterTextLink
              key={String(l.id ?? `${l.link_text}-${l.priority}`)}
              label={String(l.link_text || "").trim()}
              href={String(l.link_href || "")}
              textColor={textColor}
            />
          ))}
        </div>,
      );
      continue;
    }

    if (rt === "social_icon") {
      const socials: FooterRow[] = [];
      while (j < chunk.length && String(chunk[j].row_type) === "social_icon") {
        socials.push(chunk[j]);
        j++;
      }
      nodes.push(
        <div
          key={`${chunkKey}-soc-${j}`}
          className="flex flex-wrap justify-center gap-6 min-[1024px]:justify-start"
        >
          {socials.map((s) => (
            <SocialButton
              key={String(s.id ?? s.priority)}
              href={String(s.link_href || "")}
              icon={s.icon}
              alt={s.icon_alt}
              textColor={textColor}
            />
          ))}
        </div>,
      );
      continue;
    }

    if (rt === "rich_text") {
      const html = String(r.rich_html || "").trim();
      if (html) {
        nodes.push(
          <div
            key={`${chunkKey}-rt-${r.id ?? j}`}
            className={cn(
              "mx-auto max-w-[min(100%,320px)] text-center text-[13px] font-normal leading-normal min-[640px]:text-[14px] min-[1024px]:mx-0 min-[1024px]:text-left",
              "[&_a]:underline [&_a]:underline-offset-2 [&_p]:leading-normal [&_p+_p]:mt-2",
            )}
            style={{ color: textColor }}
            dangerouslySetInnerHTML={{ __html: html }}
          />,
        );
      }
      j++;
      continue;
    }

    if (rt === "key_value") {
      const label = String(r.kv_label || "").trim();
      const value = String(r.kv_value || "").trim();
      nodes.push(
        <div
          key={`${chunkKey}-kv-${r.id ?? j}`}
          className="flex flex-wrap justify-center gap-2 text-[13px] leading-normal min-[640px]:text-[14px] min-[1024px]:justify-start"
          style={{ color: textColor }}
        >
          {label ?
            <span className="font-semibold whitespace-nowrap">{label}</span>
          : null}
          {value ?
            <span className="min-w-0 flex-1 font-normal">{value}</span>
          : null}
        </div>,
      );
      j++;
      continue;
    }

    j++;
  }

  return (
    <div key={chunkKey} className="flex w-full flex-col gap-4">
      {nodes}
    </div>
  );
}

function renderColumn(
  rows: FooterRow[],
  accentColor: string,
  textColor: string,
) {
  const sorted = sortRows(rows);
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < sorted.length) {
    const r = sorted[i];
    const rt = String(r.row_type || "").trim();

    if (rt === "section_heading") {
      const hk = `h-${r.id ?? i}`;
      blocks.push(
        <SectionHeader
          key={hk}
          title={String(r.section_title || "")}
          accentColor={accentColor}
          textColor={textColor}
        />,
      );
      i++;
      const chunk: FooterRow[] = [];
      while (
        i < sorted.length &&
        String(sorted[i].row_type) !== "section_heading"
      ) {
        chunk.push(sorted[i]);
        i++;
      }
      if (chunk.length) {
        blocks.push(
          renderUnderHeadingChunk(chunk, textColor, `${hk}-body`),
        );
      }
      continue;
    }

    if (rt === "link") {
      blocks.push(
        <div key={`l-${r.id ?? i}`} className="min-[1024px]:pl-0.5">
          <FooterTextLink
            label={String(r.link_text || "").trim()}
            href={String(r.link_href || "")}
            textColor={textColor}
          />
        </div>,
      );
      i++;
      continue;
    }

    if (rt === "rich_text") {
      const html = String(r.rich_html || "").trim();
      if (html) {
        blocks.push(
          <div
            key={`rt-${r.id ?? i}`}
            className={cn(
              "mx-auto max-w-[min(100%,320px)] text-center text-[13px] font-normal leading-normal min-[640px]:text-[14px] min-[1024px]:mx-0 min-[1024px]:text-left",
              "[&_a]:underline [&_a]:underline-offset-2 [&_p]:leading-normal [&_p+_p]:mt-2",
            )}
            style={{ color: textColor }}
            dangerouslySetInnerHTML={{ __html: html }}
          />,
        );
      }
      i++;
      continue;
    }

    if (rt === "social_icon") {
      const socials: FooterRow[] = [];
      while (
        i < sorted.length &&
        String(sorted[i].row_type) === "social_icon"
      ) {
        socials.push(sorted[i]);
        i++;
      }
      blocks.push(
        <div
          key={`soc-${socials[0]?.id ?? i}`}
          className="flex flex-wrap justify-center gap-6 min-[1024px]:justify-start"
        >
          {socials.map((s) => (
            <SocialButton
              key={String(s.id ?? s.priority)}
              href={String(s.link_href || "")}
              icon={s.icon}
              alt={s.icon_alt}
              textColor={textColor}
            />
          ))}
        </div>,
      );
      continue;
    }

    if (rt === "key_value") {
      const label = String(r.kv_label || "").trim();
      const value = String(r.kv_value || "").trim();
      blocks.push(
        <div
          key={`kv-${r.id ?? i}`}
          className="flex flex-wrap justify-center gap-2 text-[13px] leading-normal min-[640px]:text-[14px] min-[1024px]:justify-start"
          style={{ color: textColor }}
        >
          {label ?
            <span className="font-semibold whitespace-nowrap">{label}</span>
          : null}
          {value ?
            <span className="min-w-0 flex-1 font-normal">{value}</span>
          : null}
        </div>,
      );
      i++;
      continue;
    }

    i++;
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-10 min-[1024px]:items-start min-[1024px]:max-w-[360px]">
      {blocks}
    </div>
  );
}

const AboutMegaFooter = ({ data }: { data: any }) => {
  const sectionData = data?.data ?? data;

  const {
    status,
    metadata = [],
    footer_gradient_start_color = "#2E1C81",
    footer_gradient_end_color = "#15094C",
    footer_gradient_angle = "147.3",
    footer_gradient_stop_1 = "18",
    footer_gradient_stop_2 = "58",
    footer_accent_border_color = "#346491",
    footer_text_color = "#FFFFFF",
    footer_copyright_html = "",
  } = sectionData || {};

  const rows = useMemo(() => {
    const raw: FooterRow[] =
      Array.isArray(metadata) && metadata.length > 0 ?
        (metadata as FooterRow[])
      : (ABOUT_4_SEED_METADATA as FooterRow[]);
    return raw.filter((r) => r.status !== false);
  }, [metadata]);

  const col1 = useMemo(
    () => rows.filter((r) => String(r.column_index) === "1"),
    [rows],
  );
  const col2 = useMemo(
    () => rows.filter((r) => String(r.column_index) === "2"),
    [rows],
  );
  const col3 = useMemo(
    () => rows.filter((r) => String(r.column_index) === "3"),
    [rows],
  );

  if (status === false || status === 0) {
    return null;
  }

  const hasRows = col1.length > 1 || col2.length > 1 || col3.length > 1;

  const angle = footer_gradient_angle.replace(/deg/gi, "").trim() || "147.3";
  const s1 = footer_gradient_stop_1 || "18";
  const s2 = footer_gradient_stop_2 || "58";
  const bg = `linear-gradient(${angle}deg, ${footer_gradient_start_color} ${s1}%, ${footer_gradient_end_color} ${s2}%)`;

  const copyrightHtml =
    String(footer_copyright_html || "").trim() ||
    "<p>© Copyright 2026 | All Rights Reserved</p>";

  return (
    <footer
      className={cn("[&_*]:![font-family:Poppins,sans-serif]", "w-full")}
      style={{
        background: bg,
        color: footer_text_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        className={cn(
          "mx-auto flex w-full min-w-0 max-container flex-col px-6 py-10",
          "min-[1024px]:px-10 min-[1024px]:py-12",
        )}
      >
        {hasRows ?
          <>
            <div
              className={cn(
                "flex w-full flex-col gap-12",
                "min-[1024px]:flex-row min-[1024px]:items-start min-[1024px]:justify-between min-[1024px]:gap-8 min-[1024px]:px-4",
              )}
            >
              {renderColumn(col1, footer_accent_border_color, footer_text_color)}
              {renderColumn(col2, footer_accent_border_color, footer_text_color)}
              {renderColumn(col3, footer_accent_border_color, footer_text_color)}
            </div>

            <div
              className="mt-10 h-px w-full shrink-0 bg-white/25"
              aria-hidden
            />
          </>
        : null}

        <div
          className={cn(
            "mx-auto max-w-[min(100%,900px)] text-center text-[14px] font-normal leading-normal min-[640px]:text-[15px] min-[1024px]:text-[16px]",
            hasRows && "mt-8",
            "[&_a]:underline [&_a]:underline-offset-2",
          )}
          style={{ color: footer_text_color }}
          dangerouslySetInnerHTML={{ __html: copyrightHtml }}
        />
      </div>
    </footer>
  );
};

export default AboutMegaFooter;
